import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { t } from 'ttag'
import { push, replace } from 'connected-react-router'
import { DivisionDto, GoalWithProgress, UserCompanyDto, UserProfileDto } from '../../../../services/api.types'
import { store } from '../../../../index'
import Loader from '../../../../components/Loader/Loader'
import YumEmptyState from '../../../../components/YumEmptyState/YumEmptyState'
import { getFullName } from '../../../../common/helpers/translations'
import { convertUTCToLocalDate } from '../../../../common/helpers/date'
import { deriveGroupOptions } from '../../../MyGroups/helpers/groupOptions'
import { useChatChannelList, ChannelMeta, ChatListEntry, ChatListEntryInput } from '../../hooks/useChatChannelList'
import { saveChatListScroll, peekChatListScroll, clearChatListScroll } from '../../chatListScroll'
import { getFormattedTime } from '../../../Inbox/components/UserNotification/UserNotification'
import { ChatChannelKind } from '../../../../components/YumChatView/useChatChannel'
import ProgressChips from '../../../Progress/components/ProgressChips'
import YumSearchField from '../../../../components/YumSearchField/YumSearchField'

import { ReactComponent as ChallengeIcon } from '../../../../assets/v3/icons/challenge.svg'
import { ReactComponent as GroupIcon } from '../../../../assets/v3/icons/group-active.svg'
import { ReactComponent as ChatEmptyStateIcon } from '../../../../assets/v3/icons/chat-empty-state.svg'
import { ReactComponent as ExpandIcon } from '../../../../assets/v3/icons/expand.svg'

import './ChatList.scss'

export interface IChatListOwnProps {
  activeCid?: string
}

export interface IChatListStateProps {
  userDto: UserProfileDto
  userGoals: GoalWithProgress[]
  goalsLoading: boolean
  userCompanies: UserCompanyDto[]
  divisions: DivisionDto[]
  chatListFilter: ChatChannelKind | null
  channelMeta: Record<string, ChannelMeta>
}

export interface IChatListDispatchProps {
  getCurrentCompanyGoal(payload: any): void
  getCompanyDivisions(): void
  setChatListFilter(payload: ChatChannelKind | null): void
  setChatChannelMeta(payload: Record<string, ChannelMeta>): void
}

type IChatListProps = IChatListOwnProps & IChatListStateProps & IChatListDispatchProps

// Mirrors scrollToTop() in common/helpers/scroll.ts: .layout__content is normally the mobile
// scroll container, but in the short-viewport reflow mode the window scrolls instead — handle
// both; whichever isn't the scroller is a no-op.
const layoutContent = () => document.getElementsByClassName('layout__content')[0]

// A challenge chat counts as past once the challenge has been over for a few days. The grace
// period keeps the wrap-up chatter ("congrats everyone!") in the main list while it's still
// alive; after that the chat sinks into the collapsed "Past challenges" section.
const PAST_CHALLENGE_GRACE_MS = 3 * 24 * 60 * 60 * 1000
const isPastChallenge = (entry: ChatListEntry) =>
  entry.kind === 'challenge' &&
  !!entry.endedAt &&
  convertUTCToLocalDate(new Date(entry.endedAt)).getTime() + PAST_CHALLENGE_GRACE_MS < Date.now()

const openEntry = (entry: ChatListEntry) => {
  const content = layoutContent()
  // On mobile the list pane collapses (hidden via CSS) while a conversation is open, clamping
  // the container's scrollTop — save it so the back button can restore the position. The Chat
  // menu icon clears it instead (Navbar), so nav-tab entry always starts at the top.
  saveChatListScroll(Math.max(window.scrollY, content ? content.scrollTop : 0))
  store.dispatch(push(`/chat/${entry.kind}/${entry.id}`))
}

export default function ChatList(props: IChatListProps) {
  const {
    activeCid,
    userDto,
    userGoals,
    goalsLoading,
    userCompanies,
    divisions,
    chatListFilter,
    channelMeta,
    getCurrentCompanyGoal,
    getCompanyDivisions,
    setChatListFilter,
    setChatChannelMeta,
  } = props

  const { userId, firstName, lastName, email } = userDto
  const name = getFullName(firstName, lastName, email)

  useEffect(() => {
    getCurrentCompanyGoal({})
    getCompanyDivisions()
  }, [])

  const inputs: ChatListEntryInput[] = useMemo(() => {
    const challenges: ChatListEntryInput[] = (userGoals || [])
      .filter((goal) => goal.participating && goal.chatEnabled)
      .map((goal) => ({
        kind: 'challenge',
        id: goal.id,
        name: goal.title,
        image: goal.logoUrl || undefined,
        endedAt: goal.endDate,
      }))
    const groups: ChatListEntryInput[] = deriveGroupOptions(userCompanies, divisions)
      .filter((option) => !!option.divisionId)
      .map((option) => ({
        kind: 'group' as const,
        id: option.divisionId as string,
        name: option.name,
      }))
    return [...challenges, ...groups]
  }, [userGoals, userCompanies, divisions])

  const { entries, hasEnriched } = useChatChannelList(userId, name, inputs, activeCid, channelMeta, setChatChannelMeta)

  const [searchQuery, setSearchQuery] = useState('')
  const [showPast, setShowPast] = useState(false)

  const visibleEntries = chatListFilter ? entries.filter((entry) => entry.kind === chatListFilter) : entries

  // Ended challenges sink below the live chats into a collapsed "Past challenges" section so
  // dead chats stop competing with active ones for the top of the list.
  const currentEntries = visibleEntries.filter((entry) => !isPastChallenge(entry))
  const pastEntries = visibleEntries.filter(isPastChallenge)
  const orderedEntries = [...currentEntries, ...pastEntries]

  // Search only narrows the rendered list — the auto-select/filter-follow effect below keys on
  // orderedEntries so typing a query never navigates away from the open conversation. It also
  // searches past chats, so the section auto-expands while a query is active.
  const query = searchQuery.trim().toLowerCase()
  const matchesQuery = (entry: ChatListEntry) => !query || entry.name.toLowerCase().includes(query)
  const searchedCurrent = currentEntries.filter(matchesQuery)
  const searchedPast = pastEntries.filter(matchesQuery)
  const pastExpanded = showPast || !!query

  // The open conversation was filtered out of the list (e.g. a challenge chat is open and the
  // filter switched to Groups) — it should follow the filter rather than linger hidden.
  const activeEntry = activeCid ? entries.find((entry) => entry.cid === activeCid) : undefined
  const activeHiddenByFilter = !!activeEntry && !!chatListFilter && activeEntry.kind !== chatListFilter

  // Restore the saved list scroll when the list is back on screen WITH content — keyed on the
  // painted list, not the activeCid transition, so it also survives the Chat view remounting.
  // Applied twice (now + next frame) because the iOS webview clamps the container's scrollTop
  // asynchronously when the hidden list pane's height snaps back.
  useLayoutEffect(() => {
    if (activeCid || peekChatListScroll() <= 0) return
    if (!hasEnriched || !entries.length) return
    const target = peekChatListScroll()
    const apply = () => {
      const content = layoutContent()
      if (content) content.scrollTop = target
      window.scrollTo(0, target)
    }
    apply()
    requestAnimationFrame(() => {
      apply()
      clearChatListScroll()
    })
  }, [activeCid, hasEnriched, entries.length])

  // On desktop the conversation pane is always visible, so default to the most recent chat
  // instead of an empty placeholder, and re-select when the filter hides the open one.
  // Mobile keeps showing the list (auto-opening would navigate away from it). Waits for
  // enrichment so "first" reflects the final sort order, and uses replace() so browser back
  // skips the redirect instead of looping through it.
  const initialResolveDoneRef = useRef(false)
  useEffect(() => {
    if (!hasEnriched) return
    if (!window.matchMedia('(min-width: 992px)').matches) return

    // First resolution after mount: an explicitly opened conversation (deep link) wins over
    // the sticky filter — clear the filter instead of redirecting away from the target.
    if (!initialResolveDoneRef.current) {
      initialResolveDoneRef.current = true
      if (activeCid && activeHiddenByFilter) {
        setChatListFilter(null)
        return
      }
    }

    if (activeCid && !activeHiddenByFilter) return
    if (!orderedEntries.length) {
      if (activeHiddenByFilter) store.dispatch(replace('/chat'))
      return
    }
    // Ordered current-first, so a past challenge is only auto-opened when nothing live exists.
    const first = orderedEntries[0]
    if (first.cid !== activeCid) store.dispatch(replace(`/chat/${first.kind}/${first.id}`))
  }, [activeCid, hasEnriched, orderedEntries, activeHiddenByFilter])

  // Wait for the first Stream enrichment before painting: rendering the un-enriched list
  // first would visibly re-sort a moment later once previews/timestamps arrive.
  if ((goalsLoading && !entries.length) || (entries.length > 0 && !hasEnriched)) {
    return <Loader top={true} />
  }

  if (!entries.length) {
    return <YumEmptyState text={t`No chats here yet...`} Icon={ChatEmptyStateIcon} />
  }

  return (
    <>
      <ProgressChips
        ariaLabel={t`Filter chats`}
        value={chatListFilter}
        onChange={(value) => setChatListFilter(value)}
        options={[
          { label: t`All`, value: null },
          { label: t`Challenges`, value: 'challenge' },
          { label: t`Groups`, value: 'group' },
        ]}
        style={{ marginTop: 0, marginBottom: '12px' }}
      />
      <YumSearchField
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder={t`Search chats...`}
        autoFocus={false}
        debounceTime={200}
        onClear={() => setSearchQuery('')}
        style={{ marginBottom: '12px' }}
      />
      {!searchedCurrent.length && !searchedPast.length ? (
        <YumEmptyState
          text={searchQuery.trim() ? t`No chats found...` : t`No chats here yet...`}
          Icon={ChatEmptyStateIcon}
        />
      ) : (
        // Single wrapper so the desktop list pane has ONE scroll container (Chat.scss) — the
        // current and past sections must scroll together, not as two nested scrollers.
        <div className="chat-list-scroll">
          {searchedCurrent.length > 0 && (
            <div className="chat-list">{searchedCurrent.map((entry) => renderEntry(entry, activeCid))}</div>
          )}
          {searchedPast.length > 0 && (
            <>
              <button
                type="button"
                className={`chat-list-section-toggle${pastExpanded ? ' chat-list-section-toggle--open' : ''}`}
                onClick={() => setShowPast((value) => !value)}
                aria-expanded={pastExpanded}
              >
                {t`Past challenges`} ({searchedPast.length})
                {!pastExpanded && searchedPast.some((entry) => entry.unreadCount > 0) && (
                  <span className="chat-list__unread-dot" aria-hidden="true" />
                )}
                <ExpandIcon aria-hidden="true" />
              </button>
              {pastExpanded && (
                <div className="chat-list">{searchedPast.map((entry) => renderEntry(entry, activeCid))}</div>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}

function renderEntry(entry: ChatListEntry, activeCid?: string) {
  const typeLabel = entry.kind === 'challenge' ? t`Challenge` : t`Group`
  return (
    <button
      key={entry.cid}
      type="button"
      className={`chat-list__row${entry.cid === activeCid ? ' chat-list__row--active' : ''}`}
      onClick={() => openEntry(entry)}
      aria-label={entry.unreadCount > 0 ? `${entry.name} — ${t`Unread messages`}` : entry.name}
      aria-current={entry.cid === activeCid ? 'true' : undefined}
    >
      <div className="chat-list__avatar" aria-hidden="true">
        {entry.image ? (
          <img src={entry.image} alt="" />
        ) : entry.kind === 'challenge' ? (
          <ChallengeIcon />
        ) : (
          <GroupIcon />
        )}
      </div>
      <div className="chat-list__info">
        <div className="chat-list__name">{entry.name}</div>
        <div className="chat-list__preview">
          {entry.lastMessageText ? `${typeLabel} · ${entry.lastMessageText}` : typeLabel}
        </div>
      </div>
      <div className="chat-list__meta">
        {!!entry.lastMessageAt && <div className="chat-list__time">{getFormattedTime(entry.lastMessageAt)}</div>}
        {entry.unreadCount > 0 && <div className="chat-list__unread-dot" aria-hidden="true" />}
      </div>
    </button>
  )
}
